import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import { CATEGORY_ORDER, CATEGORY_SCOPE, type MapCategory } from "@/map/config/categories";

interface CategoryRailButtonProps {
  category: MapCategory;
  isOpen: boolean;
  isEnabled: boolean;
  badge?: number;
  onToggle: () => void;
}

const CategoryRailButton = ({ category, isOpen, isEnabled, badge = 0, onToggle }: CategoryRailButtonProps) => {
  const { t } = useTranslation("map");
  const label = t(`categories.labels.${category}`);

  return (
    <button
      disabled={!isEnabled}
      onClick={onToggle}
      title={!isEnabled ? t("rail.selectMachineFirst") : label}
      className={cn(
        "group relative flex flex-col items-center justify-center gap-1.5 py-4 w-full transition-colors",
        isOpen ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
        !isEnabled && "opacity-35 cursor-not-allowed hover:bg-transparent",
      )}
    >
      {badge > 0 && (
        <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      <ChevronLeft
        className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
      />
      <span
        className="text-xs font-medium tracking-wide"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
      >
        {label}
      </span>
    </button>
  );
};

interface CategoryRailProps {
  openCategory: MapCategory | null;
  /** Video is highlighted independently (it can float, detached from openCategory). */
  videoActive?: boolean;
  /** New-alerts badge count on the Alerts item. */
  alertsBadge?: number;
  hasSelectedMachine: boolean;
  hasSelectedLocation: boolean;
  onToggle: (category: MapCategory) => void;
}

const CategoryRail = ({
  openCategory,
  videoActive = false,
  alertsBadge = 0,
  hasSelectedMachine,
  hasSelectedLocation,
  onToggle,
}: CategoryRailProps) => {
  const isCategoryOpen = (category: MapCategory) =>
    category === "video" ? videoActive : openCategory === category;
  const isEnabled = (category: MapCategory) => {
    const scope = CATEGORY_SCOPE[category];
    if (scope === "machine") return hasSelectedMachine;
    if (scope === "location") return hasSelectedLocation;
    return true; // map-level (settings)
  };

  return (
    <div className="flex flex-col h-full w-14 shrink-0 border-l bg-sidebar">
      <div className="flex flex-col flex-1">
        {CATEGORY_ORDER.map((category) => (
          <CategoryRailButton
            key={category}
            category={category}
            isOpen={isCategoryOpen(category)}
            isEnabled={isEnabled(category)}
            badge={category === "alerts" ? alertsBadge : 0}
            onToggle={() => onToggle(category)}
          />
        ))}
      </div>

      {/* Settings pinned at the bottom */}
      <div className="border-t">
        <CategoryRailButton
          category="settings"
          isOpen={openCategory === "settings"}
          isEnabled
          onToggle={() => onToggle("settings")}
        />
      </div>
    </div>
  );
};

export default CategoryRail;
